import React, { useState, useEffect } from 'react';
import api from '../api';
import { FaHome, FaBuilding, FaUsers, FaSolarPanel, FaIndustry, FaCheckCircle } from 'react-icons/fa';

const Services = () => {
  const [services, setServices] = useState([]);
  
  // --- ICON MAP ---
  // Maps DB strings ("FaHome") to React Components
  const iconMap = {
    "FaHome": <FaHome />,
    "FaBuilding": <FaBuilding />,
    "FaUsers": <FaUsers />,
    "FaSolarPanel": <FaSolarPanel />,
    "FaIndustry": <FaIndustry />
  };


  useEffect(() => {
    const fetchServices = async () => {
      try {
       const response = await api.get('/Services');
        if (response.data && response.data.length > 0) {
          setServices(response.data);
        } else {
          // Fallback Data if DB is empty
          setServices([
            {
              iconName: "FaHome", title: "Residential Rooftop Solar", colorHex: "#00D084",
              description: "Customized rooftop solar systems for homes.",
              features: [{ featureText: "Site-specific design" }, { featureText: "Subsidy support" }]
            },
            {
              iconName: "FaBuilding", title: "Commercial Solar", colorHex: "#1B3F6E",
              description: "High-capacity plants for businesses.",
              features: [{ featureText: "ROI Analysis" }, { featureText: "DISCOM compliance" }]
            }
          ]);
        }
      } catch (err) {
        console.error("Error fetching services", err);
      }
    };

    fetchServices();
  }, []);

  return (
    <section className="services-section py-5 bg-light" id="services">
      <div className="container">
        
        {/* Section Header */}
        <div className="text-center mb-5">
          <span className="badge bg-success mb-3 px-3 py-2">
            <i className="bi bi-gear-fill me-2"></i>
            Our Services
          </span>
          <h2 className="display-5 fw-bold text-dark mb-3">
            Comprehensive Solar Solutions
          </h2>
          <p className="lead text-muted">
            Engineer-designed solar systems for every need across Bihar & Jharkhand
          </p>
        </div>

        {/* Services Grid (Dynamic) */}
        <div className="row g-4">
          {services.map((service, index) => (
            <div className="col-lg-6" key={index}>
              <div 
                className="card h-100 border-0 shadow-lg service-card"
                style={{ transition: 'all 0.3s ease', position: 'relative', overflow: 'hidden' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-10px)';
                  e.currentTarget.style.boxShadow = '0 20px 50px rgba(0, 0, 0, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.1)';
                }}
              >
                {/* Top Color Bar */}
                <div 
                  style={{
                    position: 'absolute', top: 0, left: 0, right: 0, height: '5px',
                    background: `linear-gradient(90deg, ${service.colorHex} 0%, ${service.colorHex}dd 100%)`
                  }}
                ></div>

                <div className="card-body p-4">
                  {/* Icon */}
                  <div 
                    className="service-icon-wrapper d-inline-flex align-items-center justify-content-center rounded-circle mb-4"
                    style={{ 
                      width: '90px', height: '90px',
                      background: `linear-gradient(135deg, ${service.colorHex} 0%, ${service.colorHex}dd 100%)`,
                      boxShadow: `0 10px 25px ${service.colorHex}40`,
                      transition: 'transform 0.3s ease'
                    }}
                  >
                    <div style={{ fontSize: '2.8rem', color: '#ffffff' }}>
                      {/* Render Icon from Map */}
                      {iconMap[service.iconName] || <FaSolarPanel />}
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="fw-bold mb-3" style={{ color: '#1B3F6E', fontSize: '1.5rem' }}>
                    {service.title}
                  </h3>

                  {/* Description */}
                  <p className="text-muted mb-4" style={{ lineHeight: '1.7' }}>
                    {service.description}
                  </p>

                  {/* Features List */}
                  {service.features && service.features.length > 0 && (
                    <>
                      <h6 className="fw-semibold mb-3" style={{ color: service.colorHex }}>
                        <FaCheckCircle className="me-2" /> Key Features:
                      </h6>
                      <ul className="list-unstyled mb-4">
                        {service.features.map((feat, idx) => (
                          <li key={idx} className="mb-2 d-flex align-items-start">
                            <i 
                              className="bi bi-check-circle-fill me-2 mt-1" 
                              style={{ color: service.colorHex, fontSize: '1rem' }}
                            ></i>
                            <span className="text-muted" style={{ fontSize: '0.95rem' }}>
                              {/* Handle both string list (if simple) or object list from DB */}
                              {typeof feat === 'string' ? feat : feat.featureText}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}

                  {/* CTA Button */}
                  <a 
                    href="#contact" 
                    className="btn w-100 rounded-pill py-2"
                    style={{
                      background: 'transparent',
                      border: `2px solid ${service.colorHex}`,
                      color: service.colorHex,
                      fontWeight: '600',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = service.colorHex;
                      e.currentTarget.style.color = '#ffffff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = service.colorHex;
                    }}
                  >
                    Get Free Quote <i className="bi bi-arrow-right ms-2"></i>
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* --- Static Bottom Content (Same as before) --- */}
        <div className="row mt-5">
          <div className="col-lg-10 mx-auto">
            <div className="card border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #E8F8F3 0%, #E3F2FD 100%)' }}>
              <div className="card-body p-4">
                <div className="row align-items-center">
                  <div className="col-md-8">
                    <h5 className="fw-bold mb-2" style={{ color: '#1B3F6E' }}>
                      <i className="bi bi-shield-check me-2" style={{ color: '#00D084' }}></i>
                      All Services Include:
                    </h5>
                    <ul className="list-inline mb-0">
                      <li className="list-inline-item me-4"><i className="bi bi-check-circle-fill text-success me-2"></i><small className="text-muted">Engineering-first approach</small></li>
                      <li className="list-inline-item me-4"><i className="bi bi-check-circle-fill text-success me-2"></i><small className="text-muted">DISCOM coordination</small></li>
                      <li className="list-inline-item"><i className="bi bi-check-circle-fill text-success me-2"></i><small className="text-muted">Lifetime support</small></li>
                    </ul>
                  </div>
                  <div className="col-md-4 text-md-end mt-3 mt-md-0">
                    <span className="badge bg-success px-3 py-2"><i className="bi bi-award-fill me-2"></i>SBPDCL/NBPDCL/JBVNL Empanelled</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center mt-5">
          <div className="card border-0 shadow-lg" style={{ background: 'linear-gradient(135deg, #1B3F6E 0%, #2E5C8A 100%)' }}>
            <div className="card-body p-5">
              <div className="row align-items-center">
                <div className="col-lg-8 text-lg-start text-center mb-4 mb-lg-0">
                  <h3 className="text-white fw-bold mb-3"><i className="bi bi-question-circle me-2"></i>Not Sure Which System is Right for You?</h3>
                  <p className="text-white-50 mb-0" style={{ fontSize: '1.05rem' }}>Our NIT & DTU trained solar engineers will conduct a <strong className="text-white">free site survey</strong> and recommend the best solution.</p>
                </div>
                <div className="col-lg-4 text-lg-end text-center">
                  <a href="#contact" className="btn btn-lg rounded-pill px-5 py-3" style={{ background: 'linear-gradient(135deg, #00D084 0%, #00A86B 100%)', color: '#ffffff', border: 'none', fontWeight: '600' }}>
                    <i className="bi bi-telephone-fill me-2"></i>Talk to an Engineer
                  </a>
                  <p className="text-white-50 small mt-2 mb-0">Call: <strong className="text-white">+91-9031654981</strong></p>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default Services;